<template>
  <div class="app-container">
    <Header :show-scroll-progress="true" />
    <div class="blog-content-container">
      <div :class="mainContentClass">
        <BlogHeader
          :title="title"
          :subheading="subheading"
          :date="date"
          :author="author"
          :author-avatar="authorAvatar"
          :reading-time="readingTime"
          :hero-image="heroImage"
        />

        <div class="table-of-contents">
          <h3>Table of Contents</h3>
          <nav class="toc-nav">
            <ul class="toc-list">
              <li>
                <a
                  href="#pre-flight"
                  @click.prevent="scrollToSection('pre-flight')"
                  >I. Pre-Flight Preparation</a
                >
                <ul>
                  <li>
                    <a
                      href="#strong-internet"
                      @click.prevent="scrollToSection('strong-internet')"
                      >1. Ensure a Strong Internet Connection</a
                    >
                  </li>
                  <li>
                    <a
                      href="#dji-settings"
                      @click.prevent="scrollToSection('dji-settings')"
                      >2. Optimize DJI App Settings</a
                    >
                  </li>
                  <li>
                    <a
                      href="#camera-gear"
                      @click.prevent="scrollToSection('camera-gear')"
                      >3. Set Camera and Drone Gear</a
                    >
                  </li>
                  <li>
                    <a
                      href="#audio-setup"
                      @click.prevent="scrollToSection('audio-setup')"
                      >4. Configure Audio Setup</a
                    >
                  </li>
                  <li>
                    <a
                      href="#pre-flight-checklist"
                      @click.prevent="scrollToSection('pre-flight-checklist')"
                      >5. Final Pre-Flight Checklist</a
                    >
                  </li>
                </ul>
              </li>
              <li>
                <a
                  href="#during-flight"
                  @click.prevent="scrollToSection('during-flight')"
                  >II. During the Live Flight</a
                >
                <ul>
                  <li>
                    <a
                      href="#connection-stability"
                      @click.prevent="scrollToSection('connection-stability')"
                      >1. Maintain Connection & Stream Stability</a
                    >
                  </li>
                  <li>
                    <a
                      href="#fly-smoothly"
                      @click.prevent="scrollToSection('fly-smoothly')"
                      >2. Fly Smoothly and Deliberately</a
                    >
                  </li>
                  <li>
                    <a
                      href="#client-engagement"
                      @click.prevent="scrollToSection('client-engagement')"
                      >3. Engage with the Client</a
                    >
                  </li>
                  <li>
                    <a
                      href="#stay-safe"
                      @click.prevent="scrollToSection('stay-safe')"
                      >4. Stay Safe and Focused</a
                    >
                  </li>
                  <li>
                    <a
                      href="#adapt-fly"
                      @click.prevent="scrollToSection('adapt-fly')"
                      >5. Adapt on the Fly</a
                    >
                  </li>
                </ul>
              </li>
              <li>
                <a
                  href="#post-flight"
                  @click.prevent="scrollToSection('post-flight')"
                  >III. Post-Flight Wrap-Up</a
                >
                <ul>
                  <li>
                    <a
                      href="#ending-session"
                      @click.prevent="scrollToSection('ending-session')"
                      >1. Ending the Session Gracefully</a
                    >
                  </li>
                  <li>
                    <a
                      href="#verify-media"
                      @click.prevent="scrollToSection('verify-media')"
                      >2. Verify Media and Data</a
                    >
                  </li>
                  <li>
                    <a
                      href="#client-debrief"
                      @click.prevent="scrollToSection('client-debrief')"
                      >3. Immediate Client Debrief</a
                    >
                  </li>
                  <li>
                    <a
                      href="#whats-next"
                      @click.prevent="scrollToSection('whats-next')"
                      >4. Highlight What's Next</a
                    >
                  </li>
                  <li>
                    <a
                      href="#follow-up"
                      @click.prevent="scrollToSection('follow-up')"
                      >5. Follow-Up Deliverables</a
                    >
                  </li>
                </ul>
              </li>
              <li>
                <a
                  href="#conclusion"
                  @click.prevent="scrollToSection('conclusion')"
                  >Conclusion</a
                >
              </li>
            </ul>
          </nav>
        </div>

        <div class="pilot-playbook-blog">
          <section class="blog-section">
            <p>
              Live drone streaming is an amazing way to let clients virtually
              join a flight in real time, but it only works well if the pilot's
              setup is dialed in. Preparation determines outcome – a
              high-quality Skyhost stream requires more than just pressing "Go
              Live." Everything from your internet connection and DJI app
              settings to camera profile and audio setup plays a role.
            </p>
            <p>
              Skyhost can deliver crystal-clear video and smooth collaboration,
              but it's only as good as the pilot's environment and
              configuration. Glitches and dropouts are usually caused by
              user-side issues (like weak signal or improper settings), not the
              platform itself. By thoroughly preparing before flight, following
              best practices during the flight, and doing smart wrap-up
              afterwards, pilots can ensure stable streams, happy clients, and
              successful live drone sessions. The goal is to make each Skyhost
              session seamless, clear, and interactive – essentially, to get the
              very best out of Skyhost for both you and your viewers.
            </p>
          </section>

          <section class="blog-section" id="pre-flight">
            <h2>I. Pre-Flight Preparation – Laying the Groundwork</h2>
            <p>
              The work you do before you ever hit the "Start Stream" button will
              largely determine how well the session goes.
            </p>

            <h3 id="strong-internet">1. Ensure a Strong Internet Connection</h3>
            <p>
              The most critical factor for live drone streaming is a strong,
              stable internet connection in the field. No matter how advanced
              Skyhost is, the outgoing video feed can only be as good as the
              bandwidth and stability you have on-site. A weak or shaky mobile
              data link will inevitably lead to stuttering, freezing, or
              pixelated video for your clients. In fact, many streaming glitches
              blamed on "the platform" are actually due to the pilot's poor
              uplink. Understanding the network requirements and limitations is
              key to preventing this.
            </p>

            <h4>Bandwidth Requirements</h4>
            <p>
              For 1080p HD live streaming, you need a significant upload speed –
              far beyond typical web browsing needs. The raw video stream from a
              drone at 1080p might be encoded around 4–8 Mbps for 30 fps video,
              or 6–12 Mbps for 60 fps. However, you should never operate at the
              bare minimum. Real-world networks fluctuate; a link that tests at
              6 Mbps might drop to 3 Mbps momentarily due to congestion or
              signal variation.
            </p>
            <p>
              In practical terms, we recommend aiming for at least 20–25 Mbps
              upload speed a safe minimum for 1080p, and ideally higher for a
              cushion. Vimeo's live streaming guide even suggests around 50 Mbps
              upload for reliable 1080p streaming, with 100 Mbps ensuring a very
              smooth experience with plenty of overhead. These figures may sound
              high, but they account for real-time reliability – remember,
              you're not just uploading a file, you're continuously sending
              video data every second. It's better to have more bandwidth than
              needed than to starve the stream and get buffering.
            </p>
            <p>
              Download speed is less critical for the pilot, but it still
              matters. The pilot's device may need to download data for the
              Skyhost session (like receiving audio from clients, or the web
              conference portion of Skyhost). A strong download speed (e.g.
              ~50–100 Mbps) ensures you can hear and see any return feeds
              without lag. Additionally, a good download speed usually
              correlates with a robust connection in general. In short, aim for
              the highest quality network you can manage in the field – for
              instance, a 5G or strong 4G LTE signal, a portable Wi-Fi hotspot
              with a fast backhaul or even a Starlink. Some guidelines suggest
              ~25 Mbps up and ~100 Mbps down as a comfortable baseline for
              hassle-free HD streaming. This level of connectivity should handle
              a 1080p drone feed plus the two-way Skyhost communication with
              ease.
            </p>

            <h4>Latency, Jitter, and Packet Loss</h4>
            <p>
              Raw bandwidth isn't the only factor. Latency (ping time) and
              jitter (variations in latency) can greatly affect real-time video.
              High latency can introduce a noticeable delay between the drone
              camera and what viewers see, making interaction sluggish. Jitter
              and packet loss can cause frames to drop or the video to
              momentarily stutter even if bandwidth is high. For instance, if
              your connection has periodic spikes in delay or drops packets, the
              stream might pause to buffer or show artifacts. Unlike watching a
              pre-recorded video, live streaming has no buffer safety net –
              everything is in real time. Thus, a stable connection with
              consistent latency is as important as raw speed. In practical
              terms, this means that a 4G connection with full bars and
              consistent performance is preferable to an intermittent 5G signal
              that swings wildly. It's wise to test not just speed, but also
              stability.
            </p>

            <h4>Testing Your Connection (Pre-Flight)</h4>
            <p>
              Because mobile internet quality can vary significantly by location
              and time of day, always test the connection at your flight site
              before going live. Use a standard speed test tool (such as
              Speedtest.net or Fast.com) to check your upload and download
              speeds. Run the test a few times to see if results are consistent
              or fluctuating. It's also helpful to test latency (many speed
              tests will show ping; you can also simply run a continuous ping in
              the background to monitor stability). If the upload speed is below
              your target (for example, you only get 5 Mbps up but you hoped for
              1080p streaming), consider lowering your streaming quality (e.g.,
              use 720p or a lower bitrate – more on that in the DJI section) or
              finding a better spot if possible. Field anecdotes suggest that
              even 5–10 Mbps upload can work for 720p, but it's risky for 1080p
              unless very steady. A quick test stream is also golden: if Skyhost
              allows a private test, try streaming for a minute to see if the
              video holds up. It's better to discover issues at 2 pm during
              setup than during the live client demo at 4 pm.
            </p>

            <h4>Improving Connectivity</h4>
            <p>
              If your tests show borderline connectivity, have a plan to boost
              it:
            </p>
            <ul class="features-list">
              <li>
                <strong>Choose the right network and location:</strong> If you
                have a device with multiple SIMs or you carry both a phone and a
                dedicated hotspot, check which carrier has better coverage at
                your location. Sometimes simply moving to a slightly higher
                elevation or away from obstructions can improve signal.
                Line-of-sight to a cell tower (no hills or big buildings in
                between) gives the best odds of a strong, low-jitter signal.
                Avoid "dead zones" known for poor reception.
              </li>
              <li>
                <strong>Use a High-Quality Hotspot/Router:</strong> Rather than
                relying on a phone's built-in hotspot (which can be weaker in
                antenna performance), consider using a dedicated 4G/5G router or
                MiFi unit if you frequently live stream. These often have better
                antennas and radio optimizations. Some even allow external
                antenna attachments or carrier aggregation that can yield better
                throughput. Additionally, they free up your phone to run the DJI
                app without handling networking for other devices.
              </li>
              <li>
                <strong>Bonding or Redundancy:</strong> For mission-critical
                streams, some pilots use bonding solutions (like Speedify or
                other bonding routers) that combine multiple connections (e.g.,
                4G + 5G, or two different carriers, or even cellular + Starlink)
                into one robust pipe. This can increase available bandwidth and
                provide failover if one link weakens. It might be overkill for
                many scenarios, but it's an option if you truly need maximum
                reliability (with the trade-off of extra hardware or software
                and possibly additional data costs).
              </li>
              <li>
                <strong>Consider Satellite Internet (Starlink):</strong> In
                remote areas with poor cellular coverage, a portable satellite
                internet setup (such as Starlink Roam/RV) can be a game-changer.
                Modern satellite services can offer 20–50 Mbps upload in many
                locations, which is enough for HD streaming, though latency will
                be higher (Starlink latency is usually 30–50 ms, which is
                actually not bad; traditional satellites are much higher). If
                using a satellite, be cautious about the latency – a half-second
                delay is okay for one-way video, but for two-way conversation it
                will cause some talk-over if not managed. Still, as a backup or
                last resort, it can enable live streaming where nothing else
                works. Just ensure you have power and time to set up the gear,
                and test it thoroughly (weather or obstructions can affect
                satellite links too).
              </li>
              <li>
                <strong>Optimize Device Settings:</strong> Simple things can
                help too. Make sure your phone/tablet isn't simultaneously
                downloading updates or syncing large files during the flight
                (turn off any heavy background data). If possible, disable VPNs
                or proxies that might route your data through far-away servers –
                those will add latency and potential throttling. Similarly, if
                on a mobile device, close other apps (particularly video apps)
                that might hog bandwidth. On some hotspots, you can enable QoS
                (Quality of Service) to prioritize the RTMP stream's traffic.
              </li>
            </ul>
            <p>
              In summary, treat your network like part of your gear – check it,
              have backups, and don't hesitate to delay a client session if you
              discover the internet is just too weak at the moment (better to
              wait 10 minutes for a better signal than to push on and deliver a
              choppy stream). Skyhost's streaming quality is capped by what you
              can upload from the field. If clients see glitches, the first
              thing to troubleshoot is your connection, not Skyhost's servers.
              By self-diagnosing and addressing connectivity issues – via
              testing, using better equipment, or adjusting quality – a pilot
              can massively improve the live stream experience.
            </p>

            <!-- <div class="blog-image-container">
              <img
                src="/blog_assets/deal.png"
                alt="Pre-flight checklist"
                class="banner"
              />
            </div> -->
          </section>

          <section class="blog-section">
            <h3 id="dji-settings">
              2. Optimize DJI App Settings for Live Streaming
            </h3>
            <p>
              DJI drones, paired with the DJI Fly app (or DJI GO 4 for older
              models), can broadcast live video via RTMP – which is exactly how
              Skyhost receives your drone feed. But using this feature
              effectively requires understanding the app settings, hardware
              compatibilities, and common pitfalls. Here are the key points for
              setting up and optimizing the DJI side of the live stream.
            </p>

            <h4>Finding and Enabling Live Streaming in DJI App</h4>
            <p>
              In DJI's apps, the live stream option is typically found in the
              settings during flight. For example, in the DJI Fly app (v1.4.12
              and later), you connect your drone and tap Go Fly > Settings (…
              menu) > Transmission > Live Streaming Platforms, then choose RTMP.
              You'll be prompted to enter an RTMP address (URL + stream key) and
              select a resolution/bitrate before you can start streaming. For
              Skyhost, you would use the custom RTMP address and stream key
              provided by the platform (Skyhost likely gives you a unique URL to
              send your drone feed to their servers). It's crucial to enter this
              URL and key exactly right – even a tiny typo will prevent the
              stream from starting. DJI's guide notes to include a "/" between
              the URL and key (e.g. rtmp://server.address:port/streamkey), and
              not to add an extra slash if the URL already ends with one.
              Double-check there are no spaces or missing characters in that
              field. If the stream doesn't start, 90% of the time it's because
              the RTMP info was entered incorrectly, or the drone isn't
              connected to the internet.
            </p>

            <h4>Supported Devices and Limitations</h4>
            <p>
              Not all DJI controllers support live streaming, and some have
              limitations on resolution:
            </p>
            <ul class="features-list">
              <li>
                If you're using a standard DJI controller that works with a
                phone/tablet (like DJI RC-N1, RC-N2, RC-N3), and running the DJI
                Fly app on your mobile device, you can live stream via RTMP with
                up to 1080p resolution (or 720p, as an option). Android devices
                allow choosing 5 Mbps or 3 Mbps video bitrate, while iOS devices
                (in DJI Fly) reportedly offer 2 Mbps or 1 Mbps options. Those
                bitrates are relatively low because DJI is being conservative to
                ensure stable streaming – 5 Mbps is the max on Android. Keep
                this in mind: even if you have huge bandwidth, the app might cap
                what it sends.
              </li>
              <li>
                If you're using a DJI RC 2, DJI RC Pro, or the older Smart
                Controller (controllers with built-in screens and Android OS),
                live streaming is supported but with some quirks. DJI
                documentation states that as of Fly app v1.6.0, you must plug in
                an external microphone to the RC Pro/RC 2/Smart Controller in
                order to start livestreaming. In other words, the app will not
                initiate the stream on those controllers unless it detects a mic
                in the 3.5mm jack (or possibly a USB microphone). This is a bit
                of a gotcha – presumably because they expect you might want to
                stream audio. Even if you don't plan to talk, the requirement
                stands. So, if using one of these all-in-one controllers, pack a
                simple cheap microphone or even a headset to plug in, otherwise
                you might be baffled why the "Start" button stays greyed out!
                Additionally, DJI RC 2 (the newer controller for Mavic 3 Pro
                etc.) is hardware-limited to 720p streaming. That means even if
                the app offers 1080p on a phone, the RC 2 controller itself will
                only output 720p for the live feed. Knowing this, set your
                expectations accordingly – the stream will be 720p max when
                using RC 2.
              </li>
              <li>
                If you have the DJI RC (non-Pro) – the lightweight controller
                with built-in screen that shipped with models like Mini 3 Pro –
                note that DJI Fly's livestreaming is not supported on DJI RC.
                This might be due to that unit's limited Android system. In such
                cases, you'd have to use the RC-N1 with a phone instead to live
                stream.
              </li>
            </ul>
            <p>
              In summary, ensure your hardware is compatible: if you rely on an
              integrated-screen controller, verify it can do RTMP (and update
              its firmware/app to the latest). Otherwise, plan to use a
              phone/tablet and the standard controller for streaming.
            </p>

            <h4>Choosing Resolution and Bitrate (Start Conservative)</h4>
            <p>
              In the DJI app's livestream settings, you'll typically choose 720p
              or 1080p, and a bitrate (as mentioned, up to 5 Mbps on Android, or
              lower on iOS). The best practice is to match these settings to
              your network's capacity. It might be tempting to always select
              1080p at the highest bitrate, but if your 4G connection can't
              sustain it consistently, you'll get freezes or big lag. A good
              strategy is to start a bit conservative, especially for an
              important client session. For example, if you tested your upload
              speed to be, say, ~8 Mbps stable, streaming 1080p at 5 Mbps might
              be fine, but you have little headroom if conditions worsen. You
              could start with 1080p/5 Mbps but be prepared to switch to 720p/3
              Mbps if you see issues. In fact, some experienced pilots
              intentionally start at 720p 3 Mbps for critical live events to
              minimize latency and risk, only moving up to 1080p in future
              sessions once they trust the network. One pilot reported that 720p
              at 3 Mbps had noticeably lower latency and "worked great" in their
              session, whereas pushing 1080p could introduce more lag. The
              mantra here: a stable 720p stream trumps a choppy 1080p stream.
              Your clients may notice resolution a bit, but they'll absolutely
              notice if the video feed keeps stopping.
            </p>
            <p>
              DJI's app does not dynamically adjust quality on the fly – it will
              send whatever fixed resolution/bitrate you set. If conditions
              change mid-flight (e.g., you move behind a hill and signal drops),
              the app won't automatically downgrade quality; the stream will
              simply start lagging or buffering. Therefore, keep an eye on the
              stream health. Skyhost's interface might show an indicator if the
              incoming stream is losing data, or you might notice on the DJI Fly
              camera view a red dot and timer indicating streaming – if that
              disappears, something's up. If things go south during the flight,
              consider landing or hovering in a safe spot and restarting the
              stream at a lower setting. It's a tough call to make in real time,
              but clients will appreciate a quick reset to get smooth video
              rather than struggling with a failing feed. Be sure to communicate
              (via audio on Skyhost) what you're doing so they know to expect a
              brief interruption.
            </p>

            <h4>Common Pitfalls and How to Avoid Them</h4>
            <p>A number of things can go wrong in the DJI streaming setup:</p>
            <ul class="features-list">
              <li>
                <strong>RTMP URL/Key Issues:</strong> As mentioned, a wrong URL
                or key = no stream. Always copy-paste carefully. If Skyhost
                provides a combined RTMP link (with key), make sure any
                necessary separations are correct (some apps have you input them
                together, others separately). DJI Fly expects one string, like
                rtmp://xx.xxxx.com/live/STREAMKEY. Include the slash only as
                needed. If after tapping "Start" you don't see the 3-2-1
                countdown or the red recording dot, this is the first thing to
                re-check.
              </li>
              <li>
                <strong>Internet Not Actually Connected:</strong> It sounds
                obvious, but ensure the device running DJI Fly actually has
                internet when you go to stream. If using a phone, verify
                cellular data is on and working (for example, run a quick web
                search in a browser or the built-in network test in Skyhost if
                available). If using a Smart Controller hooked to a Wi-Fi
                hotspot, confirm it's connected to that hotspot. It's easy in
                the rush of setup to forget to turn on the phone's hotspot or to
                have a tablet that's Wi-Fi only and not connected. DJI's guide
                reminds users to make sure the app is connected to the Internet
                (4G/5G or Wi-Fi) before starting. Without internet, the app
                might still say "Live streaming starting…" but nothing will
                reach the server.
              </li>
              <li>
                <strong>App Backgrounding / Screen Off:</strong> The DJI app
                needs to stay active. If you switch apps (say to reply a text or
                check something on the same device), the live feed might pause
                or stop. On Android, DJI Fly can sometimes run in background for
                a short time, but it's not reliable to do so. On iOS, leaving
                the app almost certainly pauses the camera feed. Prevent
                interruptions: before flying, mute notifications or enable Do
                Not Disturb, and avoid touching anything outside the DJI app
                until you're done. Also, disable auto-lock on your device (set
                it to never sleep) or at least a long timeout, so the screen
                doesn't turn off mid-flight. If the screen locks, you might lose
                the stream or even your drone connection. Keep a close eye on
                battery as well – streaming plus flying can tax a phone or
                tablet battery quickly, and you don't want the device dying
                mid-air.
              </li>
              <li>
                <strong>High Bitrate on Weak Network:</strong> We covered this,
                but to reiterate: pushing 1080p/5 Mbps on a connection that can
                barely do 5 Mbps will lead to trouble. The video may start but
                then fall way behind real-time or freeze. Always err on the side
                of a lower bitrate if unsure. The quality difference between 5
                and 3 Mbps is noticeable in some scenes, but overall a smooth 3
                Mbps feed will impress more than a choppy 5 Mbps feed.
              </li>
              <li>
                <strong
                  >Forgetting the Mic (for applicable controllers):</strong
                >
                If you use RC Pro, etc., not having a mic = stream won't start.
                It's an odd requirement, so it's worth repeating. Plug in a mic
                before hitting Start.
              </li>
            </ul>
            <p>
              In essence, think of the DJI app as your broadcast encoder – you
              have to configure it properly and keep it running smoothly during
              the flight. Practice using the livestream feature ahead of any
              high-stakes client flight. Do a dry run at home or in an open
              field: connect to a test RTMP server or an unlisted YouTube stream
              and get familiar with the steps. This practice can reveal if, for
              example, your tablet has an aggressive screen lock, or if you
              misremembered where the menu is, etc. By knowing the DJI streaming
              process inside out, you'll reduce stress during the actual job and
              be able to focus more on flying and client interaction.
            </p>

            <!-- <div class="blog-image-container">
              <img
                src="/blog_assets/deal.png"
                alt="Camera and drone gear for live streaming"
                class="banner"
              />
            </div> -->
          </section>

          <section class="blog-section">
            <h3 id="camera-gear">3. Set Camera and Drone Gear for Live</h3>
            <p>
              When flying for cinematic filming, drone pilots often use advanced
              camera settings (like D-Log color profile, high frame rates, etc.)
              to maximize post-production flexibility. However, live streaming
              is a different ballgame. You want the image to look great
              as-it-is, because there's no post-processing – your clients see
              exactly what the drone camera outputs, in real time. That means
              some of the usual "pro" settings might actually be
              counterproductive for live work. Here's how to configure your
              drone's camera for clear, viewer-friendly video during a Skyhost
              session.
            </p>

            <h4>Avoid Flat Color Profiles (D-Log/D-Cinelike) for Live Feed</h4>
            <p>
              DJI drones often have a "flat" or logarithmic color mode (D-Log,
              D-Log M, or D-Cinelike) designed to preserve dynamic range for
              later color grading. These profiles produce a low-contrast,
              desaturated image that looks washed out and bland until color
              corrections are applied. While that's fantastic for
              post-production, it's not ideal for a live stream to non-technical
              viewers. Your clients might see the greyish footage and think
              something is wrong with the camera or that the weather looks
              terrible, simply because they don't realize it's a log image
              requiring grading. Therefore, it's recommended to switch to a
              standard color profile (often labeled "Normal" in DJI settings) or
              any vivid/true-color profile that the drone offers when live
              streaming. Essentially, you want punchy, realistic colors and good
              contrast straight out of the camera so that the feed is
              immediately enjoyable. Unless you have a specific reason (and a
              means on the client side to live-grade the footage, which is
              unlikely), save D-Log for recording-only flights.
            </p>

            <h4>Aim for Natural, Well-Balanced Exposure</h4>
            <p>
              Live streaming typically calls for clear and evenly-exposed video.
              Dramatic lighting is nice, but avoid any camera settings that make
              the image hard to see. For instance, if you have been shooting in
              a very flat profile or with minimal sharpening/noise reduction
              (again for post-processing reasons), consider returning those to
              default or moderate levels for the live session so that the image
              is crisp and noise-free to the viewer. Ensure automatic exposure
              is working in your favor, or use manual exposure wisely. A common
              tip: if you're not comfortable locking exposure manually during
              dynamic lighting, use the Auto Exposure (AE) but consider using
              Exposure Compensation to slightly underexpose by, say, 0.3 or 0.7
              stops. This prevents the sky or clouds from blowing out to pure
              white, which live video codecs struggle with. Slight underexposure
              can always be brightened by the viewing screen, but overexposure
              (blown highlights) is unrecoverable detail. Many drones also have
              an overexposure warning (zebra stripes) – use that as a guide to
              adjust camera settings so important areas (like the subject or
              clouds) aren't overexposed.
            </p>

            <h4>Consistent White Balance</h4>
            <p>
              This is often overlooked. Avoid Auto White Balance (AWB) for live
              streaming if lighting conditions might change mid-flight. AWB can
              cause the color tint of the video to shift suddenly (for example,
              the scene goes from warm to cool) if you point the camera from the
              ground to the sky or if a cloud changes the color temperature of
              the scene. These shifts look unprofessional and can be confusing.
              Instead, set a fixed white balance appropriate for the scene
              (e.g., "Sunny" ~5600K for daylight, or a specific Kelvin value).
              That way, colors remain consistent throughout the stream. The only
              exception might be if you go from indoors to outdoors in one
              flight (rare), but generally, keep it fixed. Natural color that
              doesn't fluctuate will appear much better.
            </p>

            <h4>Use Optimized Picture Settings</h4>
            <p>
              If your drone allows adjusting sharpness, contrast, or noise
              reduction in its Normal profile, use moderate values. Too high
              sharpening can make the live compression show artifacts; too low
              might look soft on streaming. The DJI defaults are usually a safe
              bet for live output. The goal is a clean, sharp image that doesn't
              require any special knowledge to appreciate. Remember, the client
              may be viewing on a variety of devices (a laptop, a conference
              room TV, maybe a tablet) – you want a broadly appealing, standard
              look.
            </p>

            <h4>Frame Rate vs. Motion</h4>
            <p>
              Streaming platforms and DJI's RTMP typically will be 30 fps (or
              sometimes 24/25). Using 30 fps is standard and gives smooth
              motion. If you have the option for 60 fps in streaming (some
              setups might allow 1080p60 if bandwidth and hardware support it),
              note that 60 fps will use more bandwidth for the same quality and
              can appear "hyper-real" or less cinematic. For inspections or fast
              action, 60 fps might be nice; but for a more cinematic feel, 30
              fps is fine. The DJI Fly app, however, locks the stream to either
              30 fps in many cases (the bitrates given like 5 Mbps likely assume
              30 fps). So this may not be something you control on the stream
              (it might automatically match whatever your recording setting is,
              or possibly it's fixed by DJI's streaming implementation). Check
              the manuals – but in any case, ensure the shutter speed on your
              drone camera is set properly for the frame rate (the 180-degree
              shutter rule: shutter = 2x frame rate). If you are streaming at 30
              fps, try to have shutter ~1/60 if possible (using ND filters in
              bright light to achieve that). This produces slight motion blur
              that makes video look smooth to the human eye. If you don't have
              any ND filters and it's bright, your drone might be shooting at
              1/1000 or higher shutter – that results in very sharp, staccato
              frames that can make motion (like moving the drone or panning)
              look choppy on the live feed. It's not a deal-breaker, but it's
              one of those subtle quality improvements: using an ND16 or ND32 on
              a sunny day to get the shutter down to 1/60–1/120 can
              significantly improve the perceived video fluidity on stream.
              DJI's own guide for good streaming quality mentions using ND
              filters to control exposure and motion blur. This is a pro tip
              that your client might not articulate, but they'll subconsciously
              enjoy a more natural motion portrayal.
            </p>

            <p>
              In short, configure your drone camera for immediate gratification
              video: normal color, proper exposure, steady white balance,
              moderate settings. This way, your Skyhost viewers see a clear and
              colorful aerial feed that requires no explanation or adjustment.
              And importantly, what you see on your screen is what they see – so
              if it looks good on your controller or device, that's the image
              you're sharing. This is different from production filming where
              you might deliberately capture a flat image that doesn't look good
              until later. For live, remove that complexity.
            </p>

            <!-- <div class="blog-image-container">
              <img
                src="/blog_assets/deal.png"
                alt="Audio setup"
                class="banner"
              />
            </div> -->
          </section>

          <section class="blog-section">
            <h3 id="audio-setup">
              4. Configure Audio Setup (Pilot Communication)
            </h3>
            <p>
              A hallmark of Skyhost is the ability to not only stream your
              drone's video, but also to talk with clients in real time (like a
              live conference) while they watch. Managing audio during a live
              drone operation can be tricky: you, the pilot, are juggling flying
              and speaking; there's rotor noise around you; and the devices
              involved might conflict over microphones and speakers if not set
              up right. Here's how to achieve clear, echo-free audio
              communications with your clients during a Skyhost session, without
              compromising your control of the drone.
            </p>

            <h4>The One-Device vs Two-Device Approach</h4>
            <p>
              A fundamental decision is whether to run everything (the drone
              controls, video stream, and the Skyhost call) on a single device
              or to split them between two devices. The strong recommendation
              from experienced users is to use two separate devices whenever
              possible – one for flying & streaming, and one for the interactive
              call. Here's why:
            </p>
            <p>
              The DJI app (on your phone/tablet or controller) is already
              handling the drone control and the outbound RTMP video. If you try
              to also join a Skyhost video call on that same device (which
              involves using its microphone, speaker, camera potentially, and
              significant CPU/network for WebRTC), you risk overloading it. It
              could lead to the DJI app getting backgrounded or slowed, which
              might interrupt the stream or even flight telemetry. Also, many
              mobile OSes won't allow simultaneous use of the microphone by two
              apps. If Skyhost (in the browser or its app) has the mic, the DJI
              Fly app might not be able to capture audio for the stream, or vice
              versa. Things could conflict in unpredictable ways (one app might
              crash or audio might not work in one of them).
            </p>
            <p>
              By using a second device for the Skyhost call, you dedicate one
              device purely to keeping the drone flying and streaming, and
              another purely to talking/listening to clients. This separation
              greatly reduces technical conflicts. Think of it like a TV
              station: one system is the "broadcast camera" (your drone feed),
              and another is the "commentator's headset" (your communication
              line).
            </p>

            <h4>Practical Setup</h4>
            <p>
              A common and effective setup is: use your primary phone/tablet
              connected to the DJI controller to run the DJI Fly app and live
              stream the drone video. Then use a secondary device (a smartphone
              or a small tablet or a laptop) to join the Skyhost session as a
              participant (the "pilot participant"), which carries your voice
              and any interactive features. The Skyhost platform provides a link
              (a meeting URL) where you and your clients connect for two-way
              comms and viewing. Join that on the second device; you might
              identify yourself as the pilot in the call. Meanwhile, that same
              session on Skyhost is ingesting the RTMP feed from your drone. The
              clients see the drone video and they also see who is in the call
            </p>

            <h4>Audio Gear – Use a Headset or Earbuds</h4>
            <p>
              Audio quality is paramount for a smooth collaboration. It's highly
              recommended that on your call device (the one you're using to talk
              to clients), you use earphones or a headset of some kind,
              especially if there are multiple people or if you're outdoors.
              Here's why:
            </p>
            <ul class="features-list">
              <li>
                <strong>Echo Prevention:</strong> If you use a device's
                loudspeaker to hear the clients, your microphone could pick up
                that sound and feed it back, causing an echo loop. This is very
                jarring on a call. Using earphones means the only thing the mic
                picks up is your voice (and ambient noise), not the clients'
                voices. Most conferencing tools have echo cancellation, but with
                a lot of wind/rotor noise and an open speaker, cancellation can
                struggle. It's better to avoid it altogether with headphones.
              </li>
              <li>
                <strong>Noise Reduction:</strong> A good headset or earbuds with
                mic will help isolate your voice. For example, Apple AirPods Pro
                or similar have decent noise filtering; a wired lapel mic or
                headset mic positioned near your mouth will capture your speech
                clearly over the drone motor noise. The drone isn't extremely
                loud at the pilot's position (assuming it's a DJI Mavic or
                Phantom class, not an Inspire or heavy lift with a big roar),
                but wind and environment noise can be significant. Even a basic
                pair of smartphone earbuds with an inline mic can make a big
                difference versus the device's built-in mic.
              </li>
              <li>
                <strong>Freedom to Move:</strong> With a wireless earbud in, you
                can move around, turn your head, etc., without losing the
                ability to hear the client or speak. You won't have to glance
                down at a phone screen to read lips or messages as often if you
                can clearly hear them through an earpiece. This lets you keep
                your eyes mostly on the drone or its live view.
              </li>
            </ul>

            <h4>Audio Test Before Client Session</h4>
            <p>
              Always, always perform an audio test prior to the actual client
              meeting. This could be as simple as starting a Skyhost meeting by
              yourself on two devices (your call device and maybe a laptop) just
              to check if you hear echo or how the levels are. Or have a
              colleague/friend join a test call from elsewhere. Verify that they
              can hear you clearly, that your voice isn't getting drowned by
              wind or drone noise. Check that when they speak, you can hear them
              well in your earpiece and that that sound isn't going into your
              mic (ask if they hear themselves echo). If there is an echo,
              troubleshoot: it likely means some sound from their voice is
              feeding back – maybe your headphone is too loud and leaking into
              the mic, or maybe a second device not muted. Adjust volumes and
              mic placement. Sometimes using Push-To-Talk can help if you're in
              a noisy environment – you mute yourself when not speaking, and
              only unmute (or use a button) when you talk, so clients don't hear
              constant wind. But juggling that while flying might be cumbersome,
              so a good noise-rejecting mic is preferable to needing
              push-to-talk.
            </p>

            <p>
              In summary, treat the Skyhost session like a Zoom/Teams call that
              you happen to be on while flying. Don't try to integrate it into
              the DJI app or overlay it on the same screen; keep it separate and
              fully functional. By mentally separating "this device is my drone,
              that device is my voice", you'll run into fewer conflicts. The
              result will be: the client hears a confident, clear voice from the
              pilot, and the pilot hears the client without strain – all while
              the drone feed comes through uninterrupted. This professional
              audio setup shows the client that you're in control of the
              situation, much like a news broadcaster with an earpiece while
              reporting live. They won't hear distracting echoes or have to ask
              "Did you catch that?" repeatedly. Good audio really makes the
              experience feel high-quality and collaborative.
            </p>

            <!-- <div class="blog-image-container">
              <img
                src="/blog_assets/deal.png"
                alt="Final pre-flight checklist"
                class="banner"
              />
            </div> -->
          </section>

          <section class="blog-section">
            <h3 id="pre-flight-checklist">5. Final Pre-Flight Checklist</h3>
            <p>Before you ever hit the "Start Stream" button, ensure:</p>
            <ul class="features-list">
              <li>Internet speed check done (X Mbps up, good to go).</li>
              <li>RTMP URL entered correctly in DJI app.</li>
              <li>Streaming quality set (1080p/720p and bitrate chosen).</li>
              <li>Drone camera in normal color, exposure/white balance set.</li>
              <li>
                Mic/headset connected on call device; test call completed.
              </li>
              <li>
                Notifications off/Do Not Disturb on (to avoid interruptions on
                devices).
              </li>
              <li>All batteries topped up; backup battery ready.</li>
              <li>
                Brief the client (if possible) about what to expect (e.g., "I'll
                fly for ~20 minutes, feel free to direct me").
              </li>
              <li>Ready to fly!</li>
            </ul>
          </section>

          <section class="blog-section" id="during-flight">
            <h2>II. During the Live Flight – Executing with Excellence</h2>
            <p>
              Now for the live session itself. During the flight, you have two
              top priorities running in parallel: operational safety & flying,
              and client engagement & stream management. It's a bit like rubbing
              your belly and patting your head, but with practice it becomes
              second nature.
            </p>

            <h3 id="connection-stability">
              1. Maintain Connection & Stream Stability
            </h3>
            <p>
              Remind that even after a good pre-flight test, you should
              continually monitor the network signal. Keep an eye on your
              controller's signal bars (both drone link and cellular if shown).
              Many DJI drones show HD signal strength – if that drops, might be
              on the edge of range, which could indirectly affect your ability
              to stream (if the drone feed to you is bad, you can't stream what
              you don't receive).
            </p>
            <p>
              Mention if Skyhost or DJI Fly provides any live bitrate or "stream
              health" indicator, use that. Otherwise, pay attention to any lag
              in telemetry or hiccups. If a significant drop occurs, don't
              hesitate to adjust: e.g., climb a bit higher for better signal, or
              move to regain line-of-sight. Communicate to clients if needed:
              "I'm moving to regain signal to keep the video smooth."
            </p>
            <p>
              If the stream does buffer/freeze, reassure clients promptly that
              you see it and are fixing it. Possibly use the DJI Fly ability to
              quickly toggle to a lower resolution if things are consistently
              bad (that might require stopping/starting the stream, so decide if
              the improvement is worth that interruption).
            </p>

            <h3 id="fly-smoothly">2. Fly Smoothly and Deliberately</h3>
            <p>
              Because this is live, you want to avoid jerkiness that could be
              jarring. Use slow, steady movements, especially if the internet is
              just adequate. Quick sudden motions can pixelate more due to
              compression.
            </p>
            <p>
              Consider the viewer's experience: gentle pans, slow orbits, and
              pausing on points of interest will come across better than racing
              around. "Treat it like a live cinematography or a guided tour, not
              an FPV race."
            </p>
            <p>
              If the client requests faster motion (maybe they want to quickly
              scan a long fence line), you can increase speed but caution that
              image quality might momentarily drop with fast motion given a
              fixed bitrate (motion creates compression challenge). Find a
              balance.
            </p>
            <p>
              Use the drone's capabilities: if you have zoom and it's needed,
              use it rather than flying too close if safety is a concern. If you
              have gimbal tilt, use it to frame shots smoothly rather than
              jerking the drone around. Always keep situational awareness: avoid
              focusing so much on camera framing that you forget to watch for
              obstacles. If multitasking, it's okay to hover in place if you
              need to check something on the call, etc.
            </p>

            <h3 id="client-engagement">
              3. Engage with the Client (Audio & Interaction)
            </h3>
            <p>
              Encourage a running commentary: describe what the drone is seeing,
              especially if clients are not physically there. For instance, "Now
              we're looking at the south face of the tower. You can see some
              discoloration on the roof tiles – I'll get a bit closer."
            </p>
            <p>
              Ask questions to the clients to involve them: "Is there anything
              specific you'd like to inspect here?" or "Let me know if I'm
              moving too fast or if you'd like a different angle." This invites
              them to take control of the narrative, which they often
              appreciate.
            </p>
            <p>
              Listen to their requests and respond promptly. If a client says
              "Can we go higher to see the whole property?", you might say
              "Absolutely, ascending to 120 feet for a wider view." Keep them
              informed as you execute the request ("Alright, here is the
              overview shot, we are at 120 feet altitude.").
            </p>
            <p>
              Manage expectations in real time: if a request isn't possible,
              explain why and offer an alternative. E.g., "I can't fly further
              in that direction due to a signal limitation, but I can fly along
              the perimeter from this side to cover that area."
            </p>
            <p>
              Keep your tone calm, confident, and friendly. Remember, from their
              perspective, they might be new to drone streaming – they could be
              excited, or maybe nervous about the tech. Your composed demeanor
              will assure them everything is under control even if small issues
              occur.
            </p>

            <h3 id="stay-safe">4. Stay Safe and Focused</h3>
            <p>
              Reiterate that the pilot's priority is flight safety. The stream
              is important, but not at the expense of crashing the drone or
              violating rules. So if you need to, for example, briefly stop
              talking to navigate around a tricky tree, do so. You can politely
              say "Give me a moment…" and address it.
            </p>
            <p>
              Keep checking drone telemetry: battery level, GPS, etc. Don't let
              the battery get critically low. It's better to give a 2-minute
              warning, "We have about 2 minutes left before I must land," than
              to have a low-battery RTH kick in unexpectedly and cause
              confusion.
            </p>
            <p>
              Watch out for environmental factors: wind picking up (if the drone
              starts drifting or working hard, mention it if it affects
              movement), birds or other aircraft (if you see any, you might
              inform clients "I'm pausing here to yield to a helicopter passing
              by," for transparency and professionalism).
            </p>

            <h3 id="adapt-fly">5. Adapt on the Fly</h3>
            <p>
              Live sessions are dynamic. Emphasize that it's okay to adapt plans
              mid-flight. Maybe you planned to cover A, B, C points, but client
              is mostly interested in A and X (something you didn't plan). Be
              flexible and ready to go with their interest (as long as it's safe
              and feasible).
            </p>
            <p>
              If network conditions worsen or improve, adapt the streaming
              strategy. For example, if you notice by mid-flight that your
              connection has actually gotten stronger (maybe initial cell
              congestion cleared up), you might quietly increase bitrate or do
              more complex moves. Conversely, if it's degrading, slow down
              movements or focus on close-ups rather than wide fast passes that
              might stress the feed.
            </p>
            <p>
              Mention that on-the-spot problem solving is part of the job:
              whether it's adjusting camera settings because suddenly the sun
              came out from clouds (you might have to quickly reduce exposure to
              avoid overexposure – you can do that and narrate "I'm adjusting
              camera settings for the lighting change"), or dealing with a
              technical glitch like a gimbal recalibration (rare, but if gimbal
              went off horizon, you might have to fix it). The key is to handle
              it calmly and keep the client in the loop briefly, then continue.
            </p>

            <!-- <div class="blog-image-container">
              <img
                src="/blog_assets/deal.png"
                alt="Adapt on the fly"
                class="banner"
              />
            </div> -->
          </section>

          <section class="blog-section" id="post-flight">
            <h2>III. Post-Flight Wrap-Up – Maximizing Value After Landing</h2>
            <p>
              Once the live flight is over, there are still some important steps
              to ensure the session ends on a professional note and that the
              client gets maximum value.
            </p>

            <h3 id="ending-session">1. Ending the Session Gracefully</h3>
            <p>
              Describe a smooth landing process while continuing to engage the
              clients: "As I land, let's recap what we saw…" This avoids an
              abrupt cutoff. As soon as the aircraft is stationary, stop the
              Skyhost stream from the DJI app or desktop console, then power
              down the drone. Keeping the stream open continues to burn Skyhost
              usage minutes, so wrap any essential Q&A while the feed is still
              running and then move longer-form conversations to a separate
              meeting once the call is ended. Make the client aware of that
              structure upfront so everyone uses the live session time for tasks
              that truly need the video feed.
            </p>

            <h3 id="verify-media">2. Verify Media and Data</h3>
            <p>
              Instruct to stop any recordings on the drone and ensure files are
              saved. Suggest doing this before powering off anything. Corrupted
              video files due to early power-off can be a heartbreaker. Note
              down any key timestamps or observations from the flight (either
              mentally or literally write them). For instance, if at 10:15 in
              the video there was a critical finding, mark that. It will help
              when reviewing footage or communicating with the client later.
            </p>
            <p>
              Check if Skyhost recorded the session or if there are snapshots
              taken by clients via the platform. If so, know where those are
              saved (cloud or you have to download them).
            </p>

            <h3 id="client-debrief">3. Immediate Client Debrief</h3>
            <p>
              While things are fresh, discuss with the client: "How was the
              experience for you? Did the stream quality meet your
              expectations?" Encourage honest feedback. If something was subpar
              (e.g., minor lag at one point), acknowledge it and tell them you
              have a higher-quality local recording to share if needed (this
              demonstrates that even if live had a hiccup, you have a backup to
              ensure they get the info in high quality).
            </p>
            <p>
              Ask if they need any additional information that perhaps the live
              session didn't cover. Maybe they realize "we actually wanted a
              closer look at the east wall but forgot to ask." If it's feasible
              to go back up (with a fresh battery) you could even offer to do it
              now. If not, you can promise to include a snapshot from earlier
              footage or cover it next time.
            </p>

            <h3 id="whats-next">4. Highlight What's Next</h3>
            <p>
              Outline what you will deliver following the session. For example:
            </p>
            <ul class="features-list">
              <li>
                "I will email you a link to download the 4K video that was
                recorded on-board, by tomorrow."
              </li>
              <li>
                "I'll also extract a few high-resolution photos of the areas we
                focused on (like that cracked vent) and send those to you."
              </li>
            </ul>
            <p>
              Essentially, make sure the client knows this live experience also
              yields tangible outcomes. They're not just left with memories;
              they get files or reports.
            </p>

            <h3 id="follow-up">5. Follow-Up Deliverables</h3>
            <p>
              Send the promised items promptly—ideally the same day, or by the
              next morning at the latest. After a Skyhost session, you typically
              send clients an email with a Dropbox or Google Drive link to the
              drone's 4K video, plus 3-5 annotated photos highlighting key
              observations. This way, they have both the real-time experience
              and the detailed data for their records.
            </p>
          </section>

          <section
            class="blog-section"
            id="conclusion"
            style="border-bottom: none"
          >
            <h2>Conclusion</h2>
            <p>
              A successful Skyhost live stream isn't just about pressing
              go-live—it's the result of thorough pre-flight preparation,
              attentive in-flight management, and thoughtful post-flight
              follow-through. Each phase plays a crucial role in delivering a
              professional virtual experience that rivals being on-site.
            </p>
            <p>
              Preparation and communication are the magic ingredients. When you
              ensure a solid connection, configure your gear correctly, and keep
              the client in the loop throughout the process, Skyhost delivers
              stunning results that make clients feel as if they were right
              there on-site, guiding the drone themselves.
            </p>
            <p>
              With these best practices, you'll transform what could have been a
              shaky, glitchy feed into a professional virtual presence. Clients
              consistently come away impressed by the clarity of the video and
              the smooth collaboration—often commenting that it felt like they
              were practically there on site, guiding the drone in real-time.
            </p>
            <p>
              The technology—Skyhost and DJI drones—is powerful, but how you use
              it makes all the difference. Armed with this knowledge, you can
              confidently get the best out of Skyhost for your own drone
              operations, delivering exceptional live streaming experiences that
              exceed client expectations.
            </p>
            <p>
              Ready to transform your live streaming setup? Get started with
              <a href="https://skyhost.ai" target="_blank" class="blog-link"
                >Skyhost</a
              >
              today.
            </p>
          </section>
        </div>

        <BlogFooter
          :tags="tags"
          :title="title"
          :current-page-url="currentPageUrl"
          :related-blogs="relatedBlogs"
          @navigate-to-blog="navigateToBlog"
          @navigate-to-blogs="navigateToBlogs"
        />
      </div>
    </div>

    <Footer />
  </div>
</template>

<script>
import { isMobile } from "@/utils/helper";
import { computed } from "vue";
import Header from "@/components/organisms/Header.vue";
import Footer from "@/components/Footer.vue";
import BlogHeader from "@/components/organisms/BlogHeader.vue";
import BlogFooter from "@/components/organisms/BlogFooter.vue";
import { blogsRegistry } from "@/data/blogRegistry";

export default {
  name: "SkyhostPilotPlaybookBlog",
  components: {
    Header,
    Footer,
    BlogHeader,
    BlogFooter,
  },
  data() {
    const blogId = "pilot-playbook";
    const blog = blogsRegistry[blogId];

    return {
      id: blogId,
      title: blog.title,
      subheading: blog.subheading,
      date: blog.date,
      author: blog.author,
      authorAvatar: blog.authorAvatar,
      image: blog.image,
      heroImage: blog.image,
      excerpt: blog.excerpt,
      tags: blog.tags,
      relatedBlogs: [],
    };
  },
  computed: {
    isMobileLayout() {
      return isMobile();
    },
    currentPageUrl() {
      return `${window.location.origin}${this.$route.fullPath}`;
    },
    readingTime() {
      // Calculate reading time: average 200 words per minute
      // This is a comprehensive guide, estimate ~6000 words
      const wordCount =
        this.title.split(" ").length +
        (this.excerpt?.split(" ").length || 0) +
        6000; // Approximate content words
      return Math.ceil(wordCount / 200);
    },
  },
  methods: {
    navigateToBlog(blogId) {
      this.$router.push({
        path: `/blog/${blogId}`,
      });
    },
    navigateToBlogs() {
      this.$router.push({
        path: "/blogs",
      });
    },
    scrollToSection(id) {
      const element = document.getElementById(id);
      if (element) {
        const headerOffset = 100;
        const elementPosition = element.getBoundingClientRect().top;
        const offsetPosition =
          elementPosition + window.pageYOffset - headerOffset;

        window.scrollTo({
          top: offsetPosition,
          behavior: "smooth",
        });
      }
    },
  },
  created() {
    // Get related blogs (excluding current blog)
    const allOtherBlogs = Object.values(blogsRegistry).filter(
      (blog) => blog.id !== this.id
    );

    // First, try to get blogs with matching tags
    const matchingTagBlogs = allOtherBlogs.filter((blog) =>
      blog.tags.some((tag) => this.tags.includes(tag))
    );

    // If we have matching tag blogs, use them, otherwise use any other blogs
    const relatedBlogs =
      matchingTagBlogs.length > 0 ? matchingTagBlogs : allOtherBlogs;

    // Take up to 2 blogs
    this.relatedBlogs = relatedBlogs.slice(0, 2);
  },
  setup() {
    const mainContentClass = computed(() => {
      return isMobile() ? "mobile-blog-content" : "web-blog-content";
    });
    return {
      mainContentClass,
    };
  },
};
</script>

<style scoped>
.app-container {
  display: flex;
  flex-direction: column;
  min-height: 100vh;
  background-color: #fff;
  color: #333;
}

.blog-content-container {
  margin-top: 5rem;
}

.web-blog-content {
  max-width: 800px;
  margin: 0 auto;
  padding: 2rem;
}

.mobile-blog-content {
  padding: 1.5rem;
}

.table-of-contents {
  border-radius: 8px;
  margin: 2rem 0 3rem 0;
}

.table-of-contents h3 {
  font-family: "Inter", -apple-system, BlinkMacSystemFont, "Segoe UI",
    sans-serif;
  font-size: 1.3rem;
  font-weight: 700;
  color: #000;
  margin-bottom: 1.5rem;
  margin-top: 0;
}

.toc-nav {
  font-family: "Inter", -apple-system, BlinkMacSystemFont, "Segoe UI",
    sans-serif;
}

.toc-list {
  list-style: none;
  padding: 0;
  margin: 0;
}

.toc-list > li {
  margin-bottom: 1rem;
}

.toc-list > li > a {
  font-weight: 600;
  font-size: 1rem;
  color: #000;
  text-decoration: none;
  display: block;
  padding: 0.5rem 0;
  transition: color 0.2s ease;
}

.toc-list > li > a:hover {
  color: #000;
  text-decoration: underline;
}

.toc-list > li > ul {
  list-style: none;
  padding-left: 1.5rem;
  margin-top: 0.5rem;
}

.toc-list > li > ul > li {
  margin-bottom: 0.5rem;
}

.toc-list > li > ul > li > a {
  font-weight: 400;
  font-size: 0.9rem;
  color: #666;
  text-decoration: none;
  display: block;
  padding: 0.3rem 0;
  transition: color 0.2s ease;
}

.toc-list > li > ul > li > a:hover {
  color: #000;
  text-decoration: underline;
}

.pilot-playbook-blog {
  font-family: "Charter", "Georgia", "Cambria", serif;
  line-height: 1.8;
  color: #333;
  font-size: 21px;
}

.blog-section {
  margin-bottom: 4rem;
  padding: 2rem 0;
  border-bottom: 1px solid #f0f0f0;
}

h2 {
  font-family: "Inter", -apple-system, BlinkMacSystemFont, "Segoe UI",
    sans-serif;
  font-size: 2rem;
  margin-bottom: 1.5rem;
  color: #000;
  font-weight: 700;
  letter-spacing: -0.01em;
  line-height: 1.2;
}

h3 {
  font-family: "Inter", -apple-system, BlinkMacSystemFont, "Segoe UI",
    sans-serif;
  font-size: 1.4rem;
  margin-bottom: 1rem;
  color: #000;
  font-weight: 600;
  letter-spacing: -0.01em;
}

h4 {
  font-family: "Inter", -apple-system, BlinkMacSystemFont, "Segoe UI",
    sans-serif;
  font-size: 1.2rem;
  margin-bottom: 0.8rem;
  margin-top: 1.5rem;
  color: #000;
  font-weight: 600;
}

p {
  margin-bottom: 1.5rem;
  font-size: 21px;
  line-height: 1.8;
  font-family: "Charter", "Georgia", "Cambria", serif;
}

.features-list {
  padding-left: 1.5rem;
  margin-bottom: 1.5rem;
  font-family: "Charter", "Georgia", "Cambria", serif;
  font-size: 21px;
}

.features-list li {
  margin-bottom: 0.8rem;
  line-height: 1.8;
}

.features-list li strong {
  font-weight: 600;
}

.blog-image-container {
  margin: 2rem 0;
}

.banner {
  width: 100%;
  height: auto;
  margin-top: 1rem;
  margin-bottom: 1rem;
}

.blog-link {
  color: #1565c0;
  text-decoration: underline;
}

@media (max-width: 768px) {
  .web-blog-content {
    padding: 1.5rem;
  }

  h2 {
    font-size: 1.75rem;
  }

  h3 {
    font-size: 1.3rem;
  }

  h4 {
    font-size: 1.1rem;
  }
}
</style>
